import React, { useMemo, useState } from 'react';
import { Main } from '@klp/layout/Main';
import { IconName, Illustration } from '@klp/design/Illustrations';
import {
    Grid,
    makeStyles,
    MenuItem,
    OutlinedInput,
    Select,
} from '@material-ui/core';
import { ActionLink, Block, Heading, Typography } from '@klp/design/index';
import { ProsjektRoutes } from '../config';
import { useTranslation } from 'react-i18next';
import MUIDataTable from 'mui-datatables';
import {
    type MUIDataTableColumn,
    type MUIDataTableOptions,
} from 'mui-datatables';
import type { ProsjektFullmaktGroupBy } from '../services/types';
import type { ProsjektFullmakt } from '../services/types';
import { useGetProsjektFullmakterQuery } from '../services/rtkApi';
import { useFormatAmount } from '../hooks/useFormatAmount';

const useStyles = makeStyles((theme) => ({
    filterCard: {
        display: 'flex',
        alignItems: 'center',
    },
    groupSelect: {
        minWidth: theme.spacing(25),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
    },
}));

export default function ProsjektFullmaktView() {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const [groupBy, setGroupBy] = useState<ProsjektFullmaktGroupBy>('ingen');
    const { formatCurrencyAmount } = useFormatAmount(i18n.language);
    const { data: prosjektFullmakter } = useGetProsjektFullmakterQuery();

    const groupedData = useMemo(() => {
        if (!prosjektFullmakter) return {};

        return prosjektFullmakter.reduce<
            Record<string, Array<ProsjektFullmakt>>
        >((fullmaktGroups, current) => {
            const regionStillingKey =
                (groupBy === 'region'
                    ? current.region
                    : t(`prosjekt.table.roller.${current.stilling}`)) ||
                'Ingen';

            if (!fullmaktGroups[regionStillingKey]) {
                fullmaktGroups[regionStillingKey] = [];
            }
            fullmaktGroups[regionStillingKey].push(current);

            return fullmaktGroups;
        }, {});
    }, [groupBy, prosjektFullmakter]);

    const getGroupIcon = () => {
        switch (groupBy) {
            case 'region':
                return (
                    <Illustration icon={IconName.PIN} klpPalette="secondary" />
                );
            case 'stilling':
                return (
                    <Illustration
                        icon={IconName.PROFILE}
                        klpPalette="secondary"
                    />
                );
            default:
                return null;
        }
    };

    const tableColumns: Array<MUIDataTableColumn> = [
        {
            label: t('prosjekt.fullmakt.table.region'),
            name: 'region',
            options: {
                display: groupBy !== 'region',
                customBodyRender: (value: string | undefined) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: t('prosjekt.fullmakt.table.stilling'),
            name: 'stilling',
            options: {
                display: groupBy !== 'stilling',
                customBodyRender: (value: string | undefined) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {t(`prosjekt.table.roller.${value}`) || value}
                    </Typography>
                ),
            },
        },
        {
            label: t('prosjekt.fullmakt.table.avdeling'),
            name: 'avdeling',
            options: {
                customBodyRender: (value: string | undefined) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: t('prosjekt.fullmakt.table.innenfor'),
            name: 'innenforBudsjett',
            options: {
                customBodyRender: (value: number, tableMeta) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatCurrencyAmount(
                            value,
                            tableMeta.rowData[0] as string,
                        )}
                    </Typography>
                ),
            },
        },
        {
            label: t('prosjekt.fullmakt.table.utover'),
            name: 'utoverBudsjett',
            options: {
                customBodyRender: (value: number, tableMeta) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatCurrencyAmount(
                            value,
                            tableMeta.rowData[0] as string,
                        )}
                    </Typography>
                ),
            },
        },
        {
            label: t('prosjekt.fullmakt.table.endring'),
            name: 'endringIBudsjett',
            options: {
                customBodyRender: (value: number, tableMeta) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatCurrencyAmount(
                            value,
                            tableMeta.rowData[0] as string,
                        )}
                    </Typography>
                ),
            },
        },
    ];

    const tableOptions: MUIDataTableOptions = {
        search: false,
        download: false,
        print: false,
        viewColumns: false,
        filter: false,
        sort: true,
        selectableRows: 'none',
        textLabels: {
            body: {
                noMatch: t('tables.body.noMatch'),
                toolTip: t('tables.body.toolTip'),
                columnHeaderTooltip: (column) =>
                    t('tables.body.toolTipFor') + column.label,
            },
            pagination: {
                next: t('tables.pagination.next'),
                previous: t('tables.pagination.previous'),
                rowsPerPage: t('tables.pagination.rowsPerPage'),
                displayRows: t('tables.pagination.displayRows'),
            },
            filter: {
                all: t('tables.filter.all'),
                title: t('tables.filter.title'),
                reset: t('tables.filter.reset'),
            },
            toolbar: {
                search: t('tables.toolbar.search'),
                downloadCsv: t('tables.toolbar.downloadCsv'),
                print: t('tables.toolbar.print'),
                viewColumns: t('tables.toolbar.viewColumns'),
                filterTable: t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: t('tables.viewColumns.title'),
                titleAria: t('tables.viewColumns.titleAria'),
            },
        },
    };

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="prosjektSchema"
                    label="breadcrumbs.project.schema"
                    href={ProsjektRoutes.projectSchema()}
                />
                <Main.Crumb
                    id="prosjekt-fullmakt"
                    label="prosjekt.fullmakt.breadcrumb"
                    href={ProsjektRoutes.projectFullmakt()}
                />
            </Main.Breadcrumbs>
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="project.backButton"
                    href={ProsjektRoutes.projectSchema()}
                />
            </Main.Navigation>
            <Block marginBottom="compact">
                <Grid
                    container={true}
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                    wrap="wrap"
                >
                    <Grid item={true}>
                        <Heading
                            klpHeadingLevel="TWO"
                            klpColor="dark"
                            translation="prosjekt.fullmakt.title"
                        />
                        <Typography klpTypographyVariant="base">
                            {t('prosjekt.fullmakt.subtitle')}
                        </Typography>
                    </Grid>
                    <Grid item={true}>
                        <Block className={classes.filterCard}>
                            <Block marginRight="compact">
                                {t('prosjekt.fullmakt.groupBy')}
                            </Block>
                            <Select
                                className={classes.groupSelect}
                                id="groupBy"
                                name="groupBy"
                                value={groupBy}
                                input={<OutlinedInput />}
                                onChange={(v) =>
                                    setGroupBy(
                                        v.target
                                            .value as ProsjektFullmaktGroupBy,
                                    )
                                }
                            >
                                <MenuItem key="ingen" value="ingen">
                                    {t('prosjekt.fullmakt.noGroup')}
                                </MenuItem>
                                <MenuItem key="region" value="region">
                                    {t('prosjekt.fullmakt.table.region')}
                                </MenuItem>
                                <MenuItem key="stilling" value="stilling">
                                    {t('prosjekt.fullmakt.table.stilling')}
                                </MenuItem>
                            </Select>
                        </Block>
                    </Grid>
                </Grid>
            </Block>

            {groupBy === 'ingen' ? (
                <Block marginTop="compact">
                    <MUIDataTable
                        title={
                            <Typography klpHeadingLevel="THREE">
                                {t('prosjekt.fullmakt.table.allTitle')}
                            </Typography>
                        }
                        columns={tableColumns}
                        options={tableOptions}
                        data={prosjektFullmakter || []}
                    />
                </Block>
            ) : (
                Object.entries(groupedData).map(([group, items]) => (
                    <Block marginTop="compact" key={group}>
                        <MUIDataTable
                            title={
                                <Block className={classes.tableTitle}>
                                    <Block marginRight="tiny">
                                        {getGroupIcon()}
                                    </Block>
                                    <Typography klpHeadingLevel="THREE">
                                        {group}
                                    </Typography>
                                </Block>
                            }
                            columns={tableColumns}
                            options={tableOptions}
                            data={items}
                        />
                    </Block>
                ))
            )}
        </Main>
    );
}
